import { useEffect, useState } from "react";
import { Preferences } from "@capacitor/preferences";
import { useAuth } from "@/features/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Wordmark } from "@/components/editorial";

/**
 * Current version of the legal disclaimer.
 * Increment this to force re-acknowledgement on existing users.
 */
const DISCLAIMER_VERSION = 1;

const PREF_KEY_ACK = "bc.disclaimer_acknowledged_at";
const PREF_KEY_VER = "bc.disclaimer_version";

/**
 * Full-screen modal shown once per disclaimer version.
 * Blocks the UI until the user taps "J'ai compris".
 */
export function DisclaimerModal() {
  const { user, isLoading: authLoading } = useAuth();
  const [visible, setVisible] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Don't check until auth state is resolved and user is logged in
    if (authLoading) return;
    if (!user) {
      setChecking(false);
      return;
    }

    async function check() {
      const { value: ack } = await Preferences.get({ key: PREF_KEY_ACK });
      const { value: ver } = await Preferences.get({ key: PREF_KEY_VER });
      const version = ver ? Number.parseInt(ver, 10) : 0;
      // Show if never acknowledged or version is outdated
      if (!ack || version < DISCLAIMER_VERSION) {
        setVisible(true);
      }
      setChecking(false);
    }
    void check();
  }, [user, authLoading]);

  async function handleAccept() {
    await Preferences.set({
      key: PREF_KEY_ACK,
      value: new Date().toISOString(),
    });
    await Preferences.set({
      key: PREF_KEY_VER,
      value: String(DISCLAIMER_VERSION),
    });
    setVisible(false);
  }

  if (checking || !visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm">
      <div className="mx-auto flex max-h-[90vh] w-full max-w-lg flex-col overflow-y-auto px-6 py-10">
        {/* Header */}
        <div className="mb-6 text-center">
          <Wordmark size="sm" />
        </div>

        <h1 className="font-serif text-[26px] font-normal leading-[1.2] text-foreground">
          Mentions légales
        </h1>
        <p className="mt-1 text-[12px] text-ink-50">
          Dernière mise à jour : 17 mai 2026
        </p>

        {/* Body */}
        <div className="mt-6 space-y-5 text-[14px] leading-relaxed text-ink-80">
          <section>
            <h2 className="mb-1.5 font-semibold text-foreground">
              1. Génération par intelligence artificielle
            </h2>
            <p>
              Les recettes, conseils de conservation, estimations de durée et
              autres contenus présentés sur Batch Cooking sont générés par
              intelligence artificielle (IA). Bien que nous nous efforçons de
              fournir des informations pertinentes et de qualité, ces contenus
              peuvent contenir des erreurs, des omissions ou des imprécisions.
            </p>
          </section>

          <section>
            <h2 className="mb-1.5 font-semibold text-foreground">
              2. Estimations de durée de conservation
            </h2>
            <p>
              Les durées de conservation indiquées sont des{" "}
              <strong>estimations indicatives</strong>. Elles ne constituent pas
              une garantie de fraîcheur ou de sécurité alimentaire. Il vous
              incombe de vérifier l'état des aliments avant consommation.
            </p>
          </section>

          <section>
            <h2 className="mb-1.5 font-semibold text-foreground">
              3. Allergies et restrictions alimentaires
            </h2>
            <p>
              La base de données et les modèles d'IA peuvent ne pas avoir
              connaissance de tous les allergènes possibles.{" "}
              <strong>
                Il est de votre entière responsabilité de vérifier la composition
                de chaque recette
              </strong>{" "}
              et de vous assurer qu'elle est adaptée à vos besoins alimentaires.
            </p>
          </section>

          <section>
            <h2 className="mb-1.5 font-semibold text-foreground">
              4. Absence de conseil médical ou nutritionnel
            </h2>
            <p>
              Les informations nutritionnelles fournies le sont à titre indicatif
              uniquement. Batch Cooking ne fournit pas de conseil médical et ne
              saurait se substituer à un avis professionnel.
            </p>
          </section>

          <section>
            <h2 className="mb-1.5 font-semibold text-foreground">
              5. Limitation de responsabilité
            </h2>
            <p>
              Batch Cooking décline toute responsabilité pour tout dommage
              résultant de l'utilisation des contenus générés, notamment les
              intoxications alimentaires, réactions allergiques ou détérioration
              d'aliments.
            </p>
          </section>

          <section>
            <h2 className="mb-1.5 font-semibold text-foreground">
              6. Acceptation
            </h2>
            <p>
              En utilisant Batch Cooking, vous reconnaissez avoir lu, compris et
              accepté les termes de cette clause de non-responsabilité.
            </p>
          </section>
        </div>

        {/* CTA */}
        <div className="mt-8">
          <Button size="lg" className="w-full" onClick={handleAccept}>
            J'ai compris
          </Button>
          <p className="mt-2 text-center text-[11px] text-ink-30">
            En cliquant, vous confirmez avoir pris connaissance de ces
            informations.
          </p>
        </div>
      </div>
    </div>
  );
}
